import { IValidationFunction } from "aic";
import { eHierarchyLevel } from "./api/enums";

/**
 * Regex patterns for validating hierarchy tags at each level, matching the backend validation rules:
 * - TA: exactly 2 or 3 uppercase letters
 * - Project: D followed by 3 digits, then optional letters, digits, spaces, underscores, or hyphens
 * - Study: D followed by 3 digits, 2 uppercase alphanumeric characters, then exactly 5 digits
 */
const tagPatterns: Partial<Record<eHierarchyLevel, RegExp>> = {
  [eHierarchyLevel.TA]: /^[A-Z]{2,3}$/,
  [eHierarchyLevel.Project]: /^D\d{3}[A-Za-z0-9 _-]*$/,
  [eHierarchyLevel.Study]: /^D[0-9]{3}[A-Z0-9]{2}[0-9]{5}$/,
};

/**
 * Creates a validation function for hierarchy tags that validates format and uniqueness entirely
 * on the frontend using regex patterns and sibling tag lists.
 *
 * Format rules (matching backend):
 * - TA: 2 or 3 uppercase letters
 * - Project: D followed by 3 digits, then optional letters, digits, spaces, underscores, or hyphens
 * - Study: D followed by 3 digits, 2 uppercase alphanumeric characters, then 5 digits
 *
 * Uniqueness is checked using `target.options.existingValues` (sibling hierarchy tags).
 *
 * @param level - The hierarchy level determining the format validation pattern.
 * @param tagDesc - The tag description label used in user-facing error messages.
 * @param helpText - The help text shown when the value is empty or fails format validation.
 * @returns A validation function that checks a tag value without calling the backend.
 */
export const checkTag =
  (level: eHierarchyLevel, tagDesc: string, helpText: string): IValidationFunction =>
  async (_backEndUrl, target, _controller) => {
    // Short-circuit if the value hasn't changed
    if (target.options?.previousValue === target.value) {
      return { success: true, data: { valid: true, validationText: "" } };
    }

    // Return help text for empty values
    if (target.value === "") {
      return { success: true, data: { valid: false, validationText: helpText } };
    }

    // Format validation using level-specific regex pattern
    const pattern = tagPatterns[level];
    if (pattern && !pattern.test(target.value)) {
      return { success: true, data: { valid: false, validationText: helpText } };
    }

    // Uniqueness check using sibling tag list from context
    if (target.options?.existingValues?.includes(target.value)) {
      return {
        success: true,
        data: { valid: false, validationText: `This ${tagDesc} is already in use` },
      };
    }

    return { success: true, data: { valid: true, validationText: "" } };
  };
